// JS-only API wrapper for interview-related GraphQL operations with retries, timeouts, and normalized outputs.
// No runtime effect until imported; safe to keep in repo before wiring.

import { START_INTERVIEW_GQL, START_AI_INTERVIEW_PART_GQL, UPDATE_INTERVIEW_GQL, STOP_INTERVIEW_GQL } from "../gql/interview.mutations";

/**
 * @typedef {Object} StartInterviewResponse
 * @property {string} recordingId
 * @property {string[]} transcripts
 * @property {number} durationSec
 * @property {number} currentSectionDurationSec
 */

/**
 * @typedef {Object} StartAiPartResponse
 * @property {string} key
 * @property {string} sectionId
 * @property {string} name
 * @property {number} sectionDurationSec
 * @property {boolean} isLast
 * @property {number} order
 */

/**
 * @typedef {Object} StopInterviewResponse
 * @property {string} recordingId
 * @property {boolean} isFinished
 */

function backoffDelay(attempt, base = 400, cap = 5000) {
  const exp = Math.min(cap, 100 + base * attempt);
  const jitter = Math.random() * 0.3 + 0.85; // 0.85x..1.15x
  return Math.floor(exp * jitter);
}

function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

function defaultClassifier(e) {
  if (e?.name === 'AbortError' || e?.code === 'timeout') return 'retry';
  if (e?.networkError) return 'retry';
  return 'fail';
}

/**
 * @param {string} code
 * @param {string} message
 * @param {any} [meta]
 */
function makeApiError(code, message, meta) {
  const err = new Error(message);
  err.code = code;
  err.meta = meta;
  return err;
}

/**
 * @param {(attempt: number, controller: AbortController) => Promise<any>} fn
 * @param {{ maxAttempts?: number, baseDelayMs?: number, classify?: (e:any)=>'retry'|'fail' }} [opts]
 */
export async function retryable(fn, opts = {}) {
  const maxAttempts = opts.maxAttempts ?? 4;
  const base = opts.baseDelayMs ?? 400;
  const classify = opts.classify ?? defaultClassifier;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const controller = new AbortController();
    try {
      return await fn(attempt, controller);
    } catch (e) {
      const type = classify(e);
      if (type === 'fail' || attempt === maxAttempts - 1) throw e;
      await delay(backoffDelay(attempt, base));
    }
  }
}

export function withTimeout(promise, controller, ms = 15000) {
  const t = setTimeout(() => controller.abort("timeout"), ms);
  return promise.finally(() => clearTimeout(t));
}

/**
 * @param {{ apolloClient: import('@apollo/client').ApolloClient<any> }} deps
 */
export function createInterviewApi({ apolloClient }) {
  const client = apolloClient;

  /**
   * @param {{ applicationId: string, part?: number }} args
   * @returns {Promise<StartAiPartResponse>}
   */
  async function startAiInterviewPart({ applicationId, sectionId = undefined }) {
    return retryable(async (attempt, controller) => {
      const res = await withTimeout(
        client.mutate({
          mutation: START_AI_INTERVIEW_PART_GQL,
          variables: { applicationId, sectionId },
          context: { fetchOptions: { signal: controller.signal } }
        }),
        controller
      );
      const section = res?.data?.startAiInterviewPart;
      if (!section?.token) throw makeApiError("MissingToken", "No section token returned", { res });
      // Backend returns duration in MINUTES; convert to seconds for client timers
      const sectionDurationSec = Math.max(0, Math.floor((section.duration || 0) * 60));
      return {
        key: section.token,
        sectionId: section._id,
        name: section.name,
        sectionDurationSec,
        isLast: Boolean(section.isLast),
        order: typeof section.order === "number" ? section.order : 0,
      };
    });
  }

  /**
   * @param {{ applicationId: string }} args
   * @returns {Promise<StartInterviewResponse>}
   */
  async function startInterview({ applicationId }) {
    return retryable(async (attempt, controller) => {
      const res = await withTimeout(
        client.mutate({
          mutation: START_INTERVIEW_GQL,
          variables: { applicationId },
          context: { fetchOptions: { signal: controller.signal } }
        }),
        controller
      );
      const data = res?.data?.startInterview;
      if (!data?.recording?._id) throw makeApiError("MissingRecording", "No recording returned", { res });
      return {
        recordingId: data.recording._id,
        transcripts: Array.isArray(data.transcripts) ? data.transcripts : [],
        durationSec: Number.isFinite(data.duration) ? data.duration : 0,
        currentSectionDurationSec: Number.isFinite(data.currentSectionDuration) ? data.currentSectionDuration : 0
      };
    });
  }

  /**
   * @param {{ recordingId: string, transcript?: string, durationSec?: number, part?: number }} args
   * @returns {Promise<void>}
   */
  async function updateInterview({ recordingId, transcript, durationSec, currentSectionId, currentSectionDurationSec }) {
    return retryable(async (attempt, controller) => {
      await withTimeout(
        client.mutate({
          mutation: UPDATE_INTERVIEW_GQL,
          variables: {
            recordingId,
            transcript: transcript ?? "",
            duration: Math.max(0, Math.floor(durationSec ?? 0)),
            currentSectionId,
            currentSectionDuration: Math.max(0, Math.floor(currentSectionDurationSec ?? 0))
          },
          context: { fetchOptions: { signal: controller.signal } }
        }),
        controller,
        12000
      );
    }, { maxAttempts: 2 });
  }

  /**
   * @param {{ recordingId: string, transcript: string, feedback: string, score: number }} args
   * @returns {Promise<StopInterviewResponse>}
   */
  async function stopInterview({ recordingId, transcript, feedback, score, currentSectionId }) {
    return retryable(async (attempt, controller) => {
      // Only send feedback/score when the caller actually provides them. With the webrtcfilter
      // relay, the backend persists these server-side from the observed interview_finished;
      // sending client defaults ("" / 0) would overwrite the observed values.
      const variables = {
        recordingId,
        transcript: transcript ?? "",
        currentSectionId
      };
      if (feedback !== undefined && feedback !== null) variables.feedback = feedback;
      if (Number.isFinite(score)) variables.score = score;
      const res = await withTimeout(
        client.mutate({
          mutation: STOP_INTERVIEW_GQL,
          variables,
          context: { fetchOptions: { signal: controller.signal } }
        }),
        controller,
        20000
      );
      const root = res?.data?.stopInterview;
      // Accept multiple possible API shapes for backward compatibility
      // Shape A: { recording: { _id } }
      const recA = root?.recording?._id;
      // Shape B: { applications: [ { interview: { isFinished, recording: { _id } } } ] }
      const apps = Array.isArray(root?.applications) ? root.applications : [];
      let matched = false;
      let isFinished = false;
      if (recA) {
        matched = recA === recordingId;
      }
      if (!matched && apps.length) {
        for (const app of apps) {
          const rId = app?.interview?.recording?._id;
          const fin = !!app?.interview?.isFinished;
          if (rId && rId === recordingId) {
            matched = true;
            isFinished = fin || true;
            break;
          }
          // If recording id absent, still accept a finished interview signal as success (best-effort)
          if (!rId && fin) {
            isFinished = true;
          }
        }
      }
      if (!matched && recA) {
        // If we got a recording id but it doesn't match, still consider isFinished truthy if present anywhere
        matched = false;
      }
      if (!matched && !isFinished) {
        throw makeApiError("StopMismatch", "Unexpected stopInterview response", { res });
      }
      return { recordingId, isFinished: true };
    }, { maxAttempts: 3 });
  }

  return { startAiInterviewPart, startInterview, updateInterview, stopInterview };
}
